// Print Preview Tab with Live Preview
// Sprint 4: Preview & Export UI

import React, { useMemo } from 'react';
import { Button } from '@/app/components/ui/button';
import { Separator } from '@/app/components/ui/separator';
import { Badge } from '@/app/components/ui/badge';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Card, CardContent } from '@/app/components/ui/card';
import { Slider } from '@/app/components/ui/slider';
import { Label } from '@/app/components/ui/label';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import type { PrintConfig } from '@/app/types/print';
import type { TableConfig } from '@/app/types/table';
import { buildExportTable, calculatePrintArea } from '@/app/utils/printUtils';
import { VALIDATION } from '@/app/utils/printConstants';
import { PAGE_SIZE_DIMENSIONS } from '@/app/types/print';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface PrintPreviewTabProps {
  config: PrintConfig;
  tableConfig: TableConfig;
  zoom: number;
  onUpdateZoom: (zoom: number) => void;
}

export function PrintPreviewTab({
  config,
  tableConfig,
  zoom,
  onUpdateZoom,
}: PrintPreviewTabProps) {
  const { t } = useLanguage();
  // Build the export table structure
  const exportTable = useMemo(() => {
    return buildExportTable(tableConfig, config.area, config.metadata);
  }, [tableConfig, config.area, config.metadata]);
  
  const { rows: selectedRows, columns: selectedColumns } = useMemo(() => {
    return calculatePrintArea(config.area, tableConfig);
  }, [config.area, tableConfig]);
  
  const handleZoomIn = () => {
    const newZoom = Math.min(zoom + 0.25, VALIDATION.MAX_ZOOM);
    onUpdateZoom(newZoom);
  };
  
  const handleZoomOut = () => {
    const newZoom = Math.max(zoom - 0.25, VALIDATION.MIN_ZOOM);
    onUpdateZoom(newZoom);
  };
  
  const handleResetZoom = () => {
    onUpdateZoom(1.0);
  };
  
  const isPDF = config.format.format === 'pdf';
  const isExcel = config.format.format === 'excel-csv';
  
  // Calculate page dimensions based on selected page size and orientation
  const pageDimensions = useMemo(() => {
    if (!isPDF || !config.format.pdf) {
      return { width: '100%', height: 'auto' };
    }
    
    const pageSize = config.format.pdf.pageSize;
    const orientation = config.format.pdf.orientation;
    
    if (pageSize === 'Auto') {
      return { width: '100%', height: 'auto' };
    }
    
    const dimensions = PAGE_SIZE_DIMENSIONS[pageSize];
    const isPortrait = orientation === 'portrait';
    
    return {
      width: `${isPortrait ? dimensions.width : dimensions.height}mm`,
      height: `${isPortrait ? dimensions.height : dimensions.width}mm`,
    };
  }, [isPDF, config.format.pdf]);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium mb-2">{t.table.printDialog.preview.title}</h3>
          <p className="text-sm text-muted-foreground">
            {t.table.printDialog.preview.subtitle}
          </p>
        </div>
        
        {/* Zoom Controls */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomOut}
              disabled={zoom <= VALIDATION.MIN_ZOOM}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-2 min-w-32">
              <Slider
                value={[zoom]}
                onValueChange={([value]) => onUpdateZoom(value)}
                min={VALIDATION.MIN_ZOOM}
                max={VALIDATION.MAX_ZOOM}
                step={0.25}
                className="w-24"
              />
              <span className="text-sm font-medium w-12 text-right">
                {Math.round(zoom * 100)}%
              </span>
            </div>
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomIn}
              disabled={zoom >= VALIDATION.MAX_ZOOM}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            
            <Button variant="outline" size="icon" onClick={handleResetZoom}>
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Export Summary */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-primary">
                {exportTable.rows.length}
              </div>
              <div className="text-xs text-muted-foreground">{t.table.printDialog.preview.rows}</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">
                {exportTable.columns.length}
              </div>
              <div className="text-xs text-muted-foreground">{t.table.printDialog.preview.cols}</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">
                {exportTable.rows.filter((r) => r.isHeader).length}
              </div>
              <div className="text-xs text-muted-foreground">{t.table.printDialog.preview.headers}</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">
                {isPDF ? 'PDF' : 'CSV'}
              </div>
              <div className="text-xs text-muted-foreground">{t.table.printDialog.preview.format}</div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Preview Area */}
      <div className="border rounded-lg bg-muted/30 p-6 overflow-auto">
        <div
          className="bg-white shadow-lg mx-auto"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease',
            width: pageDimensions.width,
            minHeight: pageDimensions.height,
            padding: isPDF ? `${config.format.pdf?.margins.top}mm` : '20px',
          }}
        >
          {/* Metadata Header (PDF only) */}
          {isPDF && config.format.pdf?.includeMetadataHeader && (
            <div className="mb-6 pb-4 border-b space-y-4">
              {/* Project Name & Description */}
              <div>
                <h1 className="text-xl font-bold mb-2">
                  {config.metadata.projectName}
                </h1>
                {config.metadata.projectDescription && (
                  <p className="text-sm text-gray-600 mb-2">
                    {config.metadata.projectDescription}
                  </p>
                )}
              </div>
              
              {/* Standard Metadata */}
              <div className="text-xs text-gray-500 space-y-1">
                {config.metadata.author && (
                  <p><strong>{t.table.printDialog.metadata.author.name}:</strong> {config.metadata.author}</p>
                )}
                {config.metadata.companyName && (
                  <p><strong>{t.table.printDialog.metadata.author.company}:</strong> {config.metadata.companyName}</p>
                )}
                <p><strong>{t.table.printDialog.metadata.project.version}:</strong> {config.metadata.version}</p>
                <p><strong>{t.table.printDialog.metadata.dates.created}:</strong> {config.metadata.dateCreated.toLocaleDateString('de-DE')}</p>
                <p><strong>{t.table.printDialog.metadata.dates.modified}:</strong> {config.metadata.dateModified.toLocaleDateString('de-DE')}</p>
                <p><strong>{t.table.printDialog.metadata.dates.printed}:</strong> {config.metadata.datePrinted.toLocaleString('de-DE')}</p>
              </div>
              
              {/* Custom Fields - Above Table (only if customFieldsDisplay is 'above-table') */}
              {config.format.pdf.customFieldsDisplay === 'above-table' && 
               config.metadata.customFields && 
               Object.keys(config.metadata.customFields).length > 0 && (
                <div className="text-xs text-gray-500 space-y-1 pt-2 border-t">
                  <p className="font-semibold text-gray-700">{t.table.printDialog.metadata.custom.title}:</p>
                  {Object.entries(config.metadata.customFields).map(([key, value]) => (
                    value && <p key={key}><strong>{key}:</strong> {value}</p>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Metadata Header (CSV with metadata option) */}
          {isExcel && config.format.excel?.includeProjectMetadata && (
            <div className="mb-6 pb-4 border-b space-y-2 font-mono text-xs">
              <div className="text-gray-700 font-semibold">
                # {t.table.printDialog.metadata.project.title.toUpperCase()}
              </div>
              <div className="text-gray-600">
                # {t.table.printDialog.metadata.project.name}: {config.metadata.projectName}
              </div>
              {config.metadata.projectDescription && (
                <div className="text-gray-600">
                  # {t.table.printDialog.metadata.project.description}: {config.metadata.projectDescription}
                </div>
              )}
              {config.metadata.author && (
                <div className="text-gray-600">
                  # {t.table.printDialog.metadata.author.name}: {config.metadata.author}
                </div>
              )}
              {config.metadata.companyName && (
                <div className="text-gray-600">
                  # {t.table.printDialog.metadata.author.company}: {config.metadata.companyName}
                </div>
              )}
              <div className="text-gray-600">
                # {t.table.printDialog.metadata.project.version}: {config.metadata.version}
              </div>
              <div className="text-gray-600">
                # {t.table.printDialog.metadata.dates.created}: {config.metadata.dateCreated.toLocaleDateString('de-DE')}
              </div>
              <div className="text-gray-600">
                # {t.table.printDialog.metadata.dates.modified}: {config.metadata.dateModified.toLocaleDateString('de-DE')}
              </div>
              <div className="text-gray-600">
                # {t.table.printDialog.metadata.dates.printed}: {config.metadata.datePrinted.toLocaleString('de-DE')}
              </div>
              
              {/* Custom Fields */}
              {config.metadata.customFields && Object.keys(config.metadata.customFields).length > 0 && (
                <>
                  <div className="text-gray-700 font-semibold pt-2">
                    # {t.table.printDialog.metadata.custom.title.toUpperCase()}
                  </div>
                  {Object.entries(config.metadata.customFields).map(([key, value]) => (
                    value && (
                      <div key={key} className="text-gray-600">
                        # {key}: {value}
                      </div>
                    )
                  ))}
                </>
              )}
              
              <div className="text-gray-700 font-semibold pt-2">
                # TABELLENDATEN
              </div>
            </div>
          )}
          
          {/* Table Preview */}
          <div className="overflow-auto">
            <table
              className="w-full border-collapse"
              style={{
                fontSize: isPDF ? `${config.format.pdf?.fontSize}px` : '14px',
                fontFamily: isPDF ? config.format.pdf?.fontFamily : 'inherit',
              }}
            >
              <tbody>
                {exportTable.rows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={row.isHeader ? 'bg-gray-700 text-white' : ''}
                  >
                    {row.cells.map((cell, cellIndex) => {
                      const Tag = row.isHeader ? 'th' : 'td';
                      return (
                        <Tag
                          key={cellIndex}
                          rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                          colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
                          className={`
                            ${
                              isPDF && config.format.pdf?.includeGridLines
                                ? 'border border-gray-300'
                                : ''
                            }
                            ${row.isHeader ? 'font-bold p-2' : 'p-2'}
                          `}
                          style={{
                            textAlign: cell.style?.textAlign || 'left',
                            backgroundColor: !row.isHeader
                              ? cell.style?.backgroundColor || 'transparent'
                              : undefined,
                            color: !row.isHeader
                              ? cell.style?.color || 'inherit'
                              : undefined,
                            fontWeight: cell.style?.bold ? 'bold' : undefined,
                            fontStyle: cell.style?.italic ? 'italic' : undefined,
                          }}
                        >
                          {String(cell.value)}
                        </Tag>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Custom Fields - Below Table (only if customFieldsDisplay is 'below-table') */}
          {isPDF && 
           config.format.pdf?.customFieldsDisplay === 'below-table' && 
           config.metadata.customFields && 
           Object.keys(config.metadata.customFields).length > 0 && (
            <div className="mt-6 pt-4 border-t space-y-1">
              <p className="text-xs font-semibold text-gray-700">{t.table.printDialog.metadata.custom.title}:</p>
              <div className="text-xs text-gray-500 space-y-1">
                {Object.entries(config.metadata.customFields).map(([key, value]) => (
                  value && <p key={key}><strong>{key}:</strong> {value}</p>
                ))}
              </div>
            </div>
          )}
          
          {/* Page Number (PDF only) */}
          {isPDF && config.format.pdf?.includePageNumbers && (
            <div
              className="mt-6 pt-4 text-xs text-gray-500"
              style={{
                textAlign:
                  config.format.pdf.pageNumberPosition === 'bottom-center'
                    ? 'center'
                    : config.format.pdf.pageNumberPosition === 'bottom-right'
                    ? 'right'
                    : 'left',
              }}
            >
              Seite 1 von 1
            </div>
          )}
        </div>
      </div>
      
      {/* Format-specific Notes */}
      <Card>
        <CardContent className="p-4">
          <div className="text-sm space-y-2">
            <div className="font-medium">{t.table.printDialog.preview.notes.title}</div>
            {isPDF && (
              <>
                <div className="text-muted-foreground">
                  {t.table.printDialog.preview.notes.pdfSize
                    .replace('{size}', String(config.format.pdf?.pageSize))
                    .replace('{orientation}', config.format.pdf?.orientation === 'portrait' ? t.table.printDialog.format.pageSetup.portrait : t.table.printDialog.format.pageSetup.landscape)
                  }
                </div>
                <div className="text-muted-foreground">
                  {t.table.printDialog.preview.notes.pdfFont
                    .replace('{font}', String(config.format.pdf?.fontFamily))
                    .replace('{size}', String(config.format.pdf?.fontSize))
                  }
                </div>
                {config.format.pdf?.includeGridLines && (
                  <div className="text-muted-foreground">
                    {t.table.printDialog.preview.notes.pdfGrid}
                  </div>
                )}
              </>
            )}
            {isExcel && (
              <>
                <div className="text-muted-foreground">
                  {t.table.printDialog.preview.notes.csvSep.replace('{sep}', config.format.excel?.separator === ',' ? t.table.printDialog.format.csv.comma : config.format.excel?.separator === ';' ? t.table.printDialog.format.csv.semicolon : t.table.printDialog.format.csv.tab)}
                </div>
                <div className="text-muted-foreground">
                  {t.table.printDialog.preview.notes.csvCompat}
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}